/**
 * Shared exercise-generation utilities.
 *
 * The cadence builder is the most important — every functional ear-training
 * exercise starts with it to establish tonality. It does light voice leading
 * so the cadence sounds like real piano accompaniment, not just block chords.
 *
 * Phase 2A widened `KeyChoice.mode` from "major" | "minor" to all seven church
 * modes. "major" and "minor" are still accepted as input aliases (mapped to
 * "ionian" / "aeolian") so Phase 1 call sites compile unchanged.
 */

import { AudioPlanBuilder } from "./audio-plan.js";
import type { Rng } from "./exercise.js";

/* ─── Modes ───────────────────────────────────────────────────────────────── */

/** Canonical mode names. `KeyChoice.mode` is always one of these seven. */
export type Mode =
  | "ionian"
  | "dorian"
  | "phrygian"
  | "lydian"
  | "mixolydian"
  | "aeolian"
  | "locrian";

/**
 * Mode-or-alias. Phase 1 call sites pass "major" / "minor" — those are
 * normalized to "ionian" / "aeolian" at the boundary.
 */
export type ModeOrAlias = Mode | "major" | "minor";

/** Semitone offsets from tonic for each mode's seven scale degrees. */
export const MODE_PATTERNS: Record<Mode, readonly number[]> = {
  ionian:     [0, 2, 4, 5, 7, 9, 11],
  dorian:     [0, 2, 3, 5, 7, 9, 10],
  phrygian:   [0, 1, 3, 5, 7, 8, 10],
  lydian:     [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  aeolian:    [0, 2, 3, 5, 7, 8, 10],
  locrian:    [0, 1, 3, 5, 6, 8, 10],
};

/** Display name shown next to the tonic letter (e.g. "C major", "C dorian"). */
export const MODE_DISPLAY_NAMES: Record<Mode, string> = {
  ionian: "major",
  dorian: "dorian",
  phrygian: "phrygian",
  lydian: "lydian",
  mixolydian: "mixolydian",
  aeolian: "minor",
  locrian: "locrian",
};

/** Normalize any accepted mode string to one of the seven canonical modes. */
export function normalizeMode(m: ModeOrAlias): Mode {
  if (m === "major") return "ionian";
  if (m === "minor") return "aeolian";
  return m;
}

/* ─── Key selection ───────────────────────────────────────────────────────── */

export interface KeyChoice {
  /** Letter A..G */
  letter: string;
  /** Accidental: -1 = flat, 0 = natural, 1 = sharp */
  accidental: number;
  /** Canonical mode (one of the seven). */
  mode: Mode;
  /** MIDI note for the tonic at a comfortable octave. */
  tonicMidi: number;
  /** Display name like "Eb major" or "D dorian". */
  name: string;
}

const COMMON_TONICS: Array<Omit<KeyChoice, "mode" | "tonicMidi" | "name">> = [
  { letter: "C", accidental: 0 }, { letter: "G", accidental: 0 },
  { letter: "D", accidental: 0 }, { letter: "A", accidental: 0 },
  { letter: "E", accidental: 0 }, { letter: "F", accidental: 0 },
  { letter: "B", accidental: -1 }, { letter: "E", accidental: -1 },
  { letter: "A", accidental: -1 }, { letter: "D", accidental: -1 },
];

const LETTER_TO_SEMI: Record<string, number> = { C:0, D:2, E:4, F:5, G:7, A:9, B:11 };

/** Pick a random key — defaults to common, ear-friendly keys. */
export function pickKey(rng: Rng, mode: ModeOrAlias = "major"): KeyChoice {
  const canonical = normalizeMode(mode);
  const t = rng.pick(COMMON_TONICS);
  // Tonic in octave 4 (C4 = 60).
  const tonicMidi = 60 + LETTER_TO_SEMI[t.letter]! + t.accidental;
  const accSym = t.accidental === -1 ? "♭" : t.accidental === 1 ? "♯" : "";
  return {
    ...t,
    mode: canonical,
    tonicMidi,
    name: `${t.letter}${accSym} ${MODE_DISPLAY_NAMES[canonical]}`,
  };
}

/* ─── Scale and chord helpers (inline copies from theory engine) ──────────── */

/** Chord intervals (in semitones above the chord's root). */
export const CHORD_INTERVALS: Record<string, number[]> = {
  // Triads
  maj:    [0, 4, 7],
  min:    [0, 3, 7],
  dim:    [0, 3, 6],
  aug:    [0, 4, 8],
  // Sevenths
  maj7:   [0, 4, 7, 11],
  min7:   [0, 3, 7, 10],
  dom7:   [0, 4, 7, 10],
  min7b5: [0, 3, 6, 10],
  dim7:   [0, 3, 6, 9],
  // 9th-chord extensions (stacked third above the seventh)
  maj9:   [0, 4, 7, 11, 14],
  min9:   [0, 3, 7, 10, 14],
  dom9:   [0, 4, 7, 10, 14],
  // 11th and 13th (jazz-leaning)
  maj11:  [0, 4, 7, 11, 14, 17],
  min11:  [0, 3, 7, 10, 14, 17],
  dom13:  [0, 4, 7, 10, 14, 17, 21],
  // Altered dominants — V7 with chromatic upper-extension alterations
  dom7b9:  [0, 4, 7, 10, 13],            // ♭9 = root + 13
  dom7s9:  [0, 4, 7, 10, 15],            // ♯9 = root + 15
  dom7s11: [0, 4, 7, 10, 14, 18],        // ♯11 = root + 18
  dom7b13: [0, 4, 7, 10, 14, 17, 20],    // ♭13 = root + 20
};

/**
 * Diatonic triad qualities by scale degree, for every mode.
 * Index `i` is the quality of the chord built on scale degree `i + 1`.
 */
export const DIATONIC_TRIAD_QUALITIES: Record<Mode, string[]> = {
  ionian:     ["maj", "min", "min", "maj", "maj", "min", "dim"],
  dorian:     ["min", "min", "maj", "maj", "min", "dim", "maj"],
  phrygian:   ["min", "maj", "maj", "min", "dim", "maj", "min"],
  lydian:     ["maj", "maj", "min", "dim", "maj", "min", "min"],
  mixolydian: ["maj", "min", "dim", "maj", "min", "min", "maj"],
  aeolian:    ["min", "dim", "maj", "min", "min", "maj", "maj"],
  locrian:    ["dim", "maj", "min", "min", "maj", "maj", "min"],
};

/** Get scale degree's root note as a MIDI offset from tonic. */
export function degreeOffset(degree: number, mode: ModeOrAlias): number {
  const pattern = MODE_PATTERNS[normalizeMode(mode)];
  return pattern[(degree - 1) % 7]!;
}

/** Build a chord (close root position) given a root MIDI and quality string. */
export function buildChord(rootMidi: number, quality: string): number[] {
  const intervals = CHORD_INTERVALS[quality];
  if (!intervals) throw new Error(`Unknown chord quality: ${quality}`);
  return intervals.map((iv) => rootMidi + iv);
}

/* ─── Voice leading ───────────────────────────────────────────────────────── */

/**
 * Pick the closest voicing of `targetTriad` to `prevVoicing`.
 * Each note can be at any octave; we minimize total movement.
 *
 * This is what makes the cadence sound like a real pianist instead of
 * mechanical block chords.
 */
export function smoothVoicing(prevVoicing: number[] | null, targetTriad: number[]): number[] {
  if (!prevVoicing) {
    // Initial voicing: spread reasonably from low to high.
    const root = targetTriad[0]!;
    const third = targetTriad[1]!;
    const fifth = targetTriad[2]!;
    const top = fifth < root + 7 ? fifth + 12 : fifth;
    return [root, third, top];
  }
  const candidates: number[][] = [];
  for (const o1 of [-12, 0, 12]) {
    for (const o2 of [-12, 0, 12]) {
      for (const o3 of [-12, 0, 12]) {
        const v = [targetTriad[0]! + o1, targetTriad[1]! + o2, targetTriad[2]! + o3].sort((a, b) => a - b);
        if (v[0]! >= 48 && v[v.length - 1]! <= 76) candidates.push(v);
      }
    }
  }
  let best = candidates[0]!;
  let bestCost = Infinity;
  for (const v of candidates) {
    let cost = 0;
    for (let i = 0; i < v.length; i++) cost += Math.abs(v[i]! - prevVoicing[i]!);
    if (cost < bestCost) { bestCost = cost; best = v; }
  }
  return best;
}

/* ─── Cadence builder ─────────────────────────────────────────────────────── */

export interface CadenceOptions {
  chordDuration?: number;
  /** Gap between chords (silence) in seconds. */
  gap?: number;
  /** Per-voice gain. */
  gain?: number;
  /** Bass-note gain (slightly louder). */
  bassGain?: number;
}


/**
 * Append a I-IV-V-I (or i-iv-V-i) cadence to a builder, with smooth voice leading.
 * Adds a bass note an octave below the root for grounding.
 *
 * Returns the time at which the cadence finishes, plus a small post-roll silence.
 *
 * Designed for ionian/aeolian context. For modal exercises that should not
 * bias the listener toward major or minor, use `appendModalContext` instead.
 */
export function appendCadence(
  builder: AudioPlanBuilder,
  key: KeyChoice,
  opts: CadenceOptions = {},
): number {
  const dur = opts.chordDuration ?? 0.85;
  const gap = opts.gap ?? 0.05;
  const gain = opts.gain ?? 0.07;
  const bassGain = opts.bassGain ?? 0.11;

  const degrees = [1, 4, 5, 1];
  const qualities = DIATONIC_TRIAD_QUALITIES[key.mode];
  let prevVoicing: number[] | null = null;

  for (const deg of degrees) {
    const rootMidi = key.tonicMidi + degreeOffset(deg, key.mode);
    const quality = qualities[deg - 1]!;
    const triad = buildChord(rootMidi, quality);
    const voicing = smoothVoicing(prevVoicing, triad);
    prevVoicing = voicing;
    builder.addNote(rootMidi - 12, dur, bassGain, "bass");
    builder.addChord(voicing, dur, gain, "harmony");
    builder.advance(dur + gap);
  }
  // Brief breath after the cadence before the target.
  builder.advance(0.25);
  return builder.now();
}

/* ─── Modal context (drone) ───────────────────────────────────────────────── */

export interface ModalContextOptions {
  /** How long the drone sustains. */
  droneDuration?: number;
  /** Gain for each drone voice. */
  gain?: number;
  /** Silence between drone end and the target audio. */
  postRoll?: number;
}

/**
 * Establish a tonic without imposing major/minor color.
 *
 * Plays a sustained tonic-octave drone (low + mid octaves of the tonic). No
 * 5th — so locrian (which has a diminished 5th) doesn't get a clashing
 * pedal, and the listener forms an unbiased sense of "this note is home"
 * before the modal scale or motif arrives. This is the modal-exercise
 * counterpart to `appendCadence`.
 */
export function appendModalContext(
  builder: AudioPlanBuilder,
  key: KeyChoice,
  opts: ModalContextOptions = {},
): number {
  const dur = opts.droneDuration ?? 2.4;
  const gain = opts.gain ?? 0.13;
  const postRoll = opts.postRoll ?? 0.45;
  const tonicLow = key.tonicMidi - 12;
  const tonicMid = key.tonicMidi;
  builder.addNote(tonicLow, dur, gain, "bass");
  builder.addNote(tonicMid, dur, gain * 0.85, "harmony");
  builder.advance(dur + postRoll);
  return builder.now();
}
